import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Add, ExpandMore, Close } from "@mui/icons-material";
import "./EditMyTechProfile.css";
import MyTechProfile from "./MyTechProfile";
import EditCertificate from "./EditCertificate";
import { useDispatch, useSelector } from "react-redux";
import {
  publishTechProfileAction,
  scrollToSpecificContentAction,
  setProfileState,
  techProfileGetUserPreviousDataAction,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import EditTechnicalAchievements from "./EditTechnicalAchievements";
import EditIcon from "@mui/icons-material/Edit";
import EditExperiences from "./EditExperience";
import EditMyTechProfilePersonalDetails from "./EditMyTechProfilePersonalDetails";
import EditMyTechProfileSkills from "./EditMyTechProfileSkills";
import EditMyTechProfileProjectDetails from "./EditMyTechProfileProjectDetails";
import EditContactDetails from "./EditContactDetails";
import MyTechProfileActionBar from "./MyTechProfileActionBar";
import { getPreviewBaseUrl } from "../../../utils/preview_link_handler";
import PublishLoader from "./PublishLoader";
import MyTechProfileSuccessModal from "./MyTechProfileSuccessModal";
import PublishConfirmationModal from "./PublishConfirmationModal";
import Confetti from "react-confetti";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { initialProfileData } from "../../../utils/constants";
import { getUserCertificationRegistrationListAction } from "../../../actions/MyCertificates/certificatesAction";
import { redirectToPageAction } from "../../../actions/userActions";

// --- Custom Dark Theme for Sidebar ---
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ffd700", // Purple for buttons and highlights
    },
    background: {
      paper: "#1e1e1e", // Background for Autocomplete, Accordion
      default: "#000000",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    },
  },
});

function useWindowSize() {
  const [size, setSize] = useState([0, 0]);
  useEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return size;
}

// --- The Main Component ---
const EditMyTechProfile = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const {
    userData,
    loading: previousDataLoading,
    success: successPreviousData,
    error,
  } = useSelector((state) => state.techProfileGetPreviousUserDataStore);
  const { loading: registrationLoading, registrations } = useSelector(
    (state) => state.userCertificatesRegistrationListStore,
  );
  const location = useLocation();
  const [showLoginWarning, setShowLoginWarning] = useState(false);
  useEffect(() => {
    if (successPreviousData) {
      dispatch(setProfileState(userData?.data));
    } else {
      let profileToSet = initialProfileData;
      if (sessionStorage.getItem("mytechprofileData")) {
        profileToSet = JSON.parse(sessionStorage.getItem("mytechprofileData"));
      }
      dispatch(setProfileState(profileToSet));
    }
    if (error) {
      // const updatedProfile = buildProfileData(
      //   initialProfileData,
      //   registrations,
      // );
      let profileToSet = initialProfileData;
      if (sessionStorage.getItem("mytechprofileData")) {
        profileToSet = JSON.parse(sessionStorage.getItem("mytechprofileData"));
      }
      dispatch(setProfileState(profileToSet));
    }
  }, [dispatch, successPreviousData, userData, error, registrations]);

  const buildProfileData = (initialProfileData, registrations) => {
    if (!registrations || registrations.length === 0) {
      return initialProfileData;
    }

    const certificatesFromRegistrations =
      mapRegistrationsToCertificates(registrations);
    console.log(certificatesFromRegistrations);
    return {
      ...initialProfileData,
      certificates:
        certificatesFromRegistrations.length > 0
          ? certificatesFromRegistrations
          : initialProfileData.certificates,
    };
  };

  const mapRegistrationsToCertificates = (registrations = []) => {
    return registrations
      .filter((reg) => reg.isActive && reg.certificate?.isIssued)
      .map((reg, index) => ({
        id: index + 1,
        title: reg.certification?.title || "Certification",
        certificateIssuedByAcetechies: true,

        // Since you only have PDF, store PDF URL here
        image: reg.certificate?.certificateUrl,

        details: reg.problem?.problemStatement?.description || "",

        techStack:
          reg.problem?.skills?.map((skill, i) => ({
            id: i + 1,
            name: skill,
            icon: null,
          })) || [],

        projectSolutions: [
          {
            id: 1,
            title: reg.problem?.title,
            details: reg.problem?.subtitle || "",
            img: reg.problem?.image?.url || null,
            githubLink: reg.submission?.githubUrl || "",
          },
        ],
      }));
  };

  useEffect(() => {
    if (userInfo) {
      dispatch(techProfileGetUserPreviousDataAction());
      dispatch(getUserCertificationRegistrationListAction());
    }
  }, [userInfo, dispatch]);

  const profileRef = useRef(null);
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const { loading, success, publishedData } = useSelector(
    (state) => state.techProfilePublishStore,
  );

  const [projects, setProjects] = useState(profile?.projects);

  // Set initial state based on whether technicalAchievements exists in the profile
  const [showTechAchievements, setShowTechAchievements] = useState(
    !!profile?.technicalAchievements,
  );
  const [showTechExperiences, setShowTechExperiences] = useState(
    !!profile?.experiences,
  );
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (profile) {
      setProjects(profile.projects || []);
      setShowTechAchievements(!!profile.technicalAchievements);
      setShowTechExperiences(!!profile.experiences);
    }
  }, [profile]);

  const history = useHistory();
  useEffect(() => {
    if (success) {
      history.push(
        `/techprofile/portfolio-activation/${publishedData?.data?._id}`,
      );
      // setIsSuccessModalOpen(true);
      // setShowConfetti(true);
      // const timer = setTimeout(() => {
      //   setShowConfetti(false);
      // }, 5000);

      // return () => clearTimeout(timer);
    }
  }, [success]);

  const scrollTo = useCallback((section) => {
    profileRef.current?.scrollTo(section);
    dispatch(scrollToSpecificContentAction(""));
  }, []);

  const handleAddTechnicalAchievementsClick = () => {
    setShowTechAchievements(true);
    if (!profile?.technicalAchievements) {
      const initialAchievements = {
        githubUsername: "",
        currentActiveTab: "github",
        platforms: [],
      };
      dispatch(
        setProfileState({
          ...profile,
          technicalAchievements: initialAchievements,
        }),
      );
    }
  };

  const handleAddTechnicalExperiencesClick = () => {
    setShowTechExperiences(true);
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setShowConfetti(false);
  };

  const handlePublishClick = () => {
    if (userInfo) {
      setIsPublishModalOpen(true);
    } else {
      setShowLoginWarning(true);
    }
  };

  // +++ Handler for the "Go Live" button click +++
  const handleGoLive = (username) => {
    // You can optionally add the username to the profile before dispatching
    const profileWithUsername = { ...profile, username };
    dispatch(publishTechProfileAction(profileWithUsername));
    setIsPublishModalOpen(false); // Close the modal
  };

  // This function now calls the handler above
  const showAddTechnicalAchievementsButton = () => (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={handleAddTechnicalAchievementsClick}
      fullWidth
      className="editmytechprofile-add-button"
    >
      Add Technical Achievements
    </Button>
  );

  const { sectionToScroll } = useSelector(
    (state) => state.TechProfileScrollToSpecificContentStore,
  );
  useEffect(() => {
    scrollTo(sectionToScroll);
  }, [sectionToScroll, scrollTo]);

  const [width, height] = useWindowSize();

  return (
    <div className="editmytechprofile-page">
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={700}
          style={{
            position: "fixed",
            top: "0px",
            left: "0px",
            zIndex: 1400, // Ensure it's above the modal backdrop
          }}
        />
      )}
      <MyTechProfileActionBar
        previewUrl={`${getPreviewBaseUrl}/profile/preview/${userInfo ? userInfo?._id : "1"}`}
        onPublish={handlePublishClick}
      />

      <div className="editmytechprofile-container">
        <div className="editmytechprofile-main-content">
          <MyTechProfile ref={profileRef} />
        </div>

        <ThemeProvider theme={darkTheme}>
          <div className="editmytechprofile-sidebar-container">
            <div className="editmytechprofile-sidebar">
              {/* --- 1. Edit Profile Details Accordion --- */}
              <Accordion
                onChange={() => scrollTo("home")}
                defaultExpanded
                className="editmytechprofile-accordion"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EditIcon sx={{ marginRight: "10px" }} />
                  <Typography>Edit Profile Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditMyTechProfilePersonalDetails scrollTo={scrollTo} />
                </AccordionDetails>
              </Accordion>

              {/* --- 2. Edit Technical Skills Accordion --- */}
              <Accordion
                onChange={() => scrollTo("skills")}
                className="editmytechprofile-accordion"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EditIcon sx={{ marginRight: "10px" }} />
                  <Typography>Edit Technical Skills</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditMyTechProfileSkills scrollTo={scrollTo} />
                </AccordionDetails>
              </Accordion>

              {showTechExperiences ? (
                <Accordion
                  onChange={() => scrollTo("experiences")}
                  className="editmytechprofile-accordion"
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <IconButton
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents accordion from toggling
                        setShowTechExperiences(false);
                        const { experiences, ...rest } = profile;
                        dispatch(setProfileState(rest));
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <Typography>Edit your Experience (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EditExperiences />
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddTechnicalExperiencesClick}
                  fullWidth
                  className="editmytechprofile-add-button"
                  sx={{ marginBottom: "20px" }}
                >
                  Add Experience
                </Button>
              )}

              {/* --- 3. Edit Project Details Accordion --- */}
              <Accordion
                onChange={() => scrollTo("projects")}
                className="editmytechprofile-accordion"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EditIcon sx={{ marginRight: "10px" }} />
                  <Typography>Edit Project Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditMyTechProfileProjectDetails scrollTo={scrollTo} />
                </AccordionDetails>
              </Accordion>

              {/* --- 4. Edit Certificate Accordion --- */}
              <Accordion
                onChange={() => scrollTo("certifications")}
                className="editmytechprofile-accordion"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EditIcon sx={{ marginRight: "10px" }} />
                  <Typography>Edit Certificate</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditCertificate
                    onStartEditing={() => scrollTo("certifications")}
                  />
                </AccordionDetails>
              </Accordion>

              {/* --- 5. Edit Technical Achievements Accordion (Optional) --- */}
              {showTechAchievements ? (
                <Accordion
                  onChange={() => scrollTo("achievements")}
                  className="editmytechprofile-accordion"
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <IconButton
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents accordion from toggling
                        setShowTechAchievements(false);
                        const { technicalAchievements, ...rest } = profile;
                        dispatch(setProfileState(rest));
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <Typography>
                      Edit Technical Achievements (Optional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EditTechnicalAchievements />
                  </AccordionDetails>
                </Accordion>
              ) : (
                showAddTechnicalAchievementsButton()
              )}
              <Accordion
                onChange={() => scrollTo("contact")}
                className="editmytechprofile-accordion"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EditIcon sx={{ marginRight: "10px" }} />
                  <Typography>Edit Contacts</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditContactDetails
                    onStartEditing={() => scrollTo("certifications")}
                  />
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </ThemeProvider>
      </div>
      {loading && <PublishLoader isOpen={loading} />}
      <ThemeProvider theme={darkTheme}>
        <PublishConfirmationModal
          open={isPublishModalOpen}
          onClose={() => setIsPublishModalOpen(false)}
          onGoLive={handleGoLive}
          previewUrlBase={`${getPreviewBaseUrl}/portfolio`}
          existingUsername={userData?.data?.username}
        />
      </ThemeProvider>
      <MyTechProfileSuccessModal
        open={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        portfolioUrl={`${getPreviewBaseUrl}/portfolio/${publishedData?.data?.username}`}
      />
      {previousDataLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(255, 255, 255, 0.04)",
            zIndex: 9999,
          }}
        >
          <CircularProgress
            size={70}
            thickness={6}
            sx={{
              color: "#fcfcfcff",
              animationDuration: "0.8s", // makes it spin faster
            }}
          />
        </Box>
      )}
      <Dialog
        open={showLoginWarning}
        onClose={() => setShowLoginWarning(false)}
        PaperProps={{
          sx: {
            background: "rgba(18, 18, 18, 0.88)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            width: "90%",
            maxWidth: "400px",
            color: "#ffffff",
            borderRadius: "18px",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
            padding: "36px 32px",
            fontFamily: "Questrial, sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.3rem",
            padding: 0,
            marginBottom: "16px",
          }}
        >
          Login Required
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          <DialogContentText
            sx={{
              color: "#cfcfcf",
              fontSize: "0.98rem",
              lineHeight: 1.6,
              marginBottom: "32px",
            }}
          >
            Your Portfolio changes have been saved successfully.
            <br />
            <br />
            Please login to publish your Tech Portfolio. You will be redirected
            back to this page after login.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            padding: 0,
          }}
        >
          {/* Primary Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setShowLoginWarning(false);

              const urlId = location?.pathname?.split("/")[3];

              dispatch(
                redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                  url: `/techprofile/edit/${urlId}`,
                }),
              );

              history.push("/login");
            }}
            sx={{
              background: "linear-gradient(90deg, #8a2be2, #a855f7)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              padding: "10px 16px",
              borderRadius: "10px",
              fontSize: "0.95rem",
              "&:hover": {
                background: "linear-gradient(90deg, #7925c9, #9333ea)",
              },
            }}
          >
            Continue to Login
          </Button>

          {/* Secondary Button */}
          <Button
            fullWidth
            onClick={() => setShowLoginWarning(false)}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid rgba(138, 43, 226, 0.6)",
              color: "#dcdcdc",
              textTransform: "none",
              padding: "10px 16px",
              borderRadius: "10px",
              fontSize: "0.9rem",
              "&:hover": {
                backgroundColor: "rgba(138, 43, 226, 0.08)",
                borderColor: "#8a2be2",
                color: "#ffffff",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditMyTechProfile;
